import { BaseHandler } from './BaseHandler';
import { IPromptOptions, IPromptGenericConfig, ModuleOptions } from '../../types';
import { libx } from 'libx.js/build/bundles/essentials.js';
import { Streams } from 'libx.js/build/modules/Streams';

export class GroqHandler extends BaseHandler {
    private baseUrl = 'https://api.groq.com/openai/v1';

    constructor(options: ModuleOptions) {
        super(options);
    }

    async execute(isStream: boolean, options: IPromptOptions): Promise<ReadableStream> {
        const isPlain = options.config.plain ?? false;
        try {
            const config = this.getConfig(options.config);
            const apiKey = this.getApiKey('groq');

            if (!apiKey) {
                throw new Error('Groq API key not configured');
            }

            const messages = this.prepareMessages(options);

            const payload: any = {
                model: config.model,
                messages,
                stream: isStream,
            };

            if (config.temperature !== undefined) payload.temperature = config.temperature;
            if (config.top_p !== undefined) payload.top_p = config.top_p;
            if (config.frequency_penalty !== undefined) payload.frequency_penalty = config.frequency_penalty;
            if (config.presence_penalty !== undefined) payload.presence_penalty = config.presence_penalty;
            if (config.stop !== undefined) payload.stop = config.stop;
            if (config.user !== undefined) payload.user = config.user;
            if (config.usesCompletionTokens) {
                payload.max_completion_tokens = config.max_tokens;
            } else {
                payload.max_tokens = config.max_tokens;
            }

            if (isStream) {
                return await this.executeStream(payload, apiKey, isPlain);
            } else {
                return await this.executeSync(payload, apiKey, isPlain);
            }
        } catch (error) {
            return this.handleError(error, isPlain);
        }
    }

    getConfig(generalConfig: IPromptGenericConfig): any {
        const rawModel = generalConfig.model || 'llama3-70b-8192';
        const model = this.normalizeModelName(rawModel, 'groq');

        // Groq uses OpenAI-compatible schema; treat like OpenAI models for token param
        const isReasoningModel = false;
        const usesCompletionTokens = false;

        let maxTokens = generalConfig.maxTokens || 2000;

        return {
            model,
            temperature: isReasoningModel ? 1 : (generalConfig.temperature ?? 0.7),
            max_tokens: maxTokens,
            top_p: isReasoningModel ? 1 : generalConfig.topP,
            frequency_penalty: generalConfig.frequencyPenalty || 0,
            presence_penalty: generalConfig.presencePenalty || 0,
            stop: generalConfig.stopSequences,
            user: generalConfig.user,
            noSystem: isReasoningModel,
            usesCompletionTokens,
        };
    }

    private prepareMessages(options: IPromptOptions): any[] {
        const messages: any[] = [];

        if (options.systemPrompt) {
            messages.push({ role: 'system', content: options.systemPrompt });
        }

        if (options.messages) {
            for (const msg of options.messages) {
                const message: any = {
                    role: msg.role,
                    content: msg.content,
                };

                if (msg.files && msg.files.length > 0) {
                    message.content = [
                        { type: 'text', text: msg.content }
                    ];
                    for (const file of msg.files) {
                        if (file.type?.startsWith('image/')) {
                            message.content.push({
                                type: 'image_url',
                                image_url: { url: file.url }
                            });
                        }
                    }
                }

                if (msg.name) message.name = msg.name;
                messages.push(message);
            }
        }

        return messages;
    }

    private async executeStream(payload: any, apiKey: string, isPlain: boolean): Promise<ReadableStream> {
        const { readable, writable } = new TransformStream();
        const writer = writable.getWriter();
        const encoder = new TextEncoder();

        const url = `${this.baseUrl}/chat/completions`;

        libx.log.v('GroqHandler: streaming request to', url);

        let faultyChunk = '';
        const promise = Streams.getStream(
            url,
            async (eventChunk: string) => {
                try {
                    const rows = eventChunk.split('\n').filter((x) => x.trim() != '');
                    for (let row of rows) {
                        if (!row.startsWith('data:')) {
                            row = faultyChunk + row;
                            faultyChunk = '';
                        }
                        const dataStr = row.slice(5).trim();
                        if (dataStr === '[DONE]') continue;
                        try {
                            const data = JSON.parse(dataStr);
                            const delta = data.choices?.[0]?.delta?.content;
                            if (delta) {
                                await this.writeStreamChunk(writer, encoder, delta, isPlain);
                            }
                        } catch (e) {
                            faultyChunk = row;
                        }
                    }
                } catch (err) {
                    libx.log.w('GroqHandler: chunk processing error', err);
                }
            },
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${apiKey}`
                },
                body: payload,
                encoding: 'utf-8',
                useEventBuffering: false,
            }
        );

        promise.then(() => {
            libx.log.v('GroqHandler: stream completed');
            if (!isPlain) {
                writer.write(encoder.encode('data: [DONE]\n\n'));
            }
            writer.close();
        }).catch((error) => {
            libx.log.e('GroqHandler: stream error', error);
            if (isPlain) {
                writer.write(encoder.encode(`\nError: ${error.message}`));
            } else {
                writer.write(encoder.encode(`data: ${JSON.stringify({ error: error.message })}\n\n`));
            }
            writer.close();
        });

        return readable;
    }

    private async executeSync(payload: any, apiKey: string, isPlain: boolean): Promise<ReadableStream> {
        const url = `${this.baseUrl}/chat/completions`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${apiKey}`
            },
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const error = await response.text();
            throw new Error(`Groq API error: ${error}`);
        }
        const data = await response.json();
        const content = data.choices?.[0]?.message?.content || '';

        const metadata = {
            model: data.model,
            usage: data.usage,
            finish_reason: data.choices?.[0]?.finish_reason
        };

        return this.writeSyncResponse(content, metadata, isPlain);
    }
}



